import { Slot } from './Slot';
import { ThemeColors } from '../Theme';

import { Global } from './Global'
import { Patron } from './Patron'
import { OptionalCost } from './OptionalCost'
import { DateConversions } from '../utils/DateConversions'

import { Permissions } from 'react-frontend-utils'
import { FormTemplate } from 'form-builder'


export class Signup {

    type;   // for Jackson, for FieldContainer typing

    category;
    maxSignups;
    requiresMembership;

    cost;       // cost per patron (name remains the same for backwards compatibility)
    isoCurrency;
    optionalCosts;
    serviceFeePercent;
    serviceFeeFixed;
    addAGSServiceFee;
    allowCashPayment;

    opensMinutesBefore;
    closesMinutesBefore;
    allowLateSignups;
    
    autoRefund;
    termsAndConditions;
    emailOnPatronSignup;
    fieldTree;              // additional questions for Patron
    about;
    requireAllPatronNames;
    restrictCheckIn;

    constructor(json) {
        if (json) {
            this.type = json.type;
            this.category = json.category;
            this.maxSignups = json.maxSignups;
            this.patronSignupEnabled = json.patronSignupEnabled;
            this.requiresMembership = json.requiresMembership;
            this.cost = json.cost;
            this.isoCurrency = json.isoCurrency;
            this.optionalCosts = json.optionalCosts ? json.optionalCosts.map(oc => new OptionalCost(oc)) : [];
            this.serviceFeePercent = json.serviceFeePercent;
            this.serviceFeeFixed = json.serviceFeeFixed;
            this.addAGSServiceFee = json.addAGSServiceFee;
            this.allowCashPayment = json.allowCashPayment;
            this.opensMinutesBefore = json.opensMinutesBefore;
            this.closesMinutesBefore = json.closesMinutesBefore;
            this.allowLateSignups = json.allowLateSignups;
            this.autoRefund = json.autoRefund;
            this.termsAndConditions = json.termsAndConditions;
            this.emailOnPatronSignup = json.emailOnPatronSignup;
            this.about = json.about;
            this.requireAllPatronNames = json.requireAllPatronNames;
            this.restrictCheckIn = json.restrictCheckIn;

            if (json.fieldTree) {  //Field tree only populated when individual Event is pulled for editing
                this.fieldTree = json.fieldTree;
                this.fieldTree.isBase = true;           //mark this as the base of the tree
            }
            else
                this.fieldTree = {type: "FieldGroup", name: "BaseFieldGroup", isBase: true, children: []};

        }
    }

    static initNew(isoCurrency) { // set defaults values for new Signup
        const signup = new Signup();
        signup.type = "Signup";
        signup.maxSignups = 1;
        signup.cost = 0;
        signup.optionalCosts = [];
        signup.isoCurrency = isoCurrency;
        signup.serviceFeeFixed = 0;
        signup.serviceFeePercent = 0;
        signup.fieldTree = {type: "FieldGroup", name: "BaseFieldGroup", isBase: true, children: []};
        return signup;
    }


    copy() {
        const copy = new Signup();
        copy.type = this.type;
        copy.category = this.category;
        copy.maxSignups = this.maxSignups;
        copy.patronSignupEnabled = this.patronSignupEnabled;
        copy.requiresMembership = this.requiresMembership;
        copy.cost = this.cost;
        copy.isoCurrency = this.isoCurrency;
        if (this.optionalCosts)
            copy.optionalCosts = this.optionalCosts.map(oc => oc.copy());
        else
            copy.optionalCosts = [];
        copy.serviceFeePercent = this.serviceFeePercent;
        copy.serviceFeeFixed = this.serviceFeeFixed;
        copy.addAGSServiceFee = this.addAGSServiceFee;
        copy.allowCashPayment = this.allowCashPayment;
        copy.opensMinutesBefore = this.opensMinutesBefore;
        copy.closesMinutesBefore = this.closesMinutesBefore;
        copy.allowLateSignups = this.allowLateSignups;
        copy.autoRefund = this.autoRefund;
        copy.termsAndConditions = this.termsAndConditions;
        copy.emailOnPatronSignup = this.emailOnPatronSignup;
        copy.about = this.about;
        copy.requireAllPatronNames = this.requireAllPatronNames;
        copy.restrictCheckIn = this.restrictCheckIn;

        if (this.fieldTree)
            copy.fieldTree = FormTemplate.copyField(this.fieldTree);
        return copy;
    }

    toJsonForPost() {
        const copy = this.copy();
        return JSON.stringify(copy);
    }


}



export class CalEvent extends Slot {

    static Visibility = {
        PUBLIC:   {enumName: "PUBLIC", displayName: "Public", description: "Visible to the public"},
        LINK:     {enumName: "LINK", displayName: "Private Link", description: "Visible only to those with a private link"},
        INTERNAL: {enumName: "INTERNAL", displayName: "Internal", description: "Internal use only, hidden for Patrons"}
    };

    owner;
    ownerName;
    visibility;

    signup;
    patronSignups;
    cancelledSignups;
    manualSignups;

    frontEndSpacesLeft;     // for patron view, when CalEvent is redacted
    locationString;         // for patron view only

    constructor(json, timezone) {
        super(json, timezone);
        if (json) {
            this.owner = json.owner;
            this.ownerName = json.ownerName;
            this.visibility = json.visibility;
            this.signup = json.signup ? new Signup(json.signup) : null;
            this.patronSignups = json.patronSignups ? json.patronSignups.map(p => new Patron(p)) : [];
            this.cancelledSignups = json.cancelledSignups ? json.cancelledSignups.map(p => new Patron(p)) : [];
            this.manualSignups = json.manualSignups ? json.manualSignups.map(p => new Patron(p)) : [];
            this.frontEndSpacesLeft = json.frontEndSpacesLeft;
            this.locationString = json.locationString;     // for display only, for patrons only
        }
    }

    static createNew(start, end, resource) {
        
        const event = new CalEvent();
        event.type = "Event";
        event.title = "Untitled Event";
        event.newEvent(start, end, resource, "ev");
        return event;
    }

    // common superclass method for creating new Event or MultiDayEvent, since they share the same fields that need to be initialized
    newEvent(start, end, resource, prefix) {
        this.owner = Global.user.id;       // the user creating the event is the owner
        this.ownerName = Global.user.name();
        this.visibility = CalEvent.Visibility.PUBLIC.enumName;
        
        // call superclass Slot method        
        this.newSlot(prefix, start, end, [resource.id]);

        this.color = resource.color ? resource.color : ThemeColors.appBarBackground;

        this.patronSignups = [];
        this.cancelledSignups = [];
        this.manualSignups = [];
    }


    copy() {
        const copy = new CalEvent();
        return this.eventCopy(copy);
    }

    // common superclass method for copying Event or MultiDayEvent, since they share the same fields that need to be copied
    eventCopy(copy) {
        super.copy(copy);
        copy.owner = this.owner;
        copy.ownerName = this.ownerName;
        copy.visibility = this.visibility;
        copy.signup = this.signup ? this.signup.copy() : null;
        copy.patronSignups = this.patronSignups.map(p => p.copy());
        copy.cancelledSignups = this.cancelledSignups.map(p => p.copy());
        copy.manualSignups = this.manualSignups.map(p => p.copy());
        return copy;
    }


    removeFieldsForNewSlot(forRecurrence) {
        super.removeFieldsForNewSlot();
        if (!forRecurrence)     // keep the signup settings if we are creating a slot for recurrence from an existing
            this.signup = null;
        this.patronSignups = [];
        this.cancelledSignups = [];
        this.manualSignups = [];
    }

    canEdit(resources) {
        
        if (Global.isResourceManager())      // resource managers can always edit Events
            return true;

        // Event can be edited if the user has permission to manage events and is the owner
        if (Global.user.hasPermissionTo(Permissions.MANAGE_EVENTS) && this.owner === Global.user.id)
            return true;        
    }

    canManageSignup() {
        return true;
    }

    refreshSignupsAndJournalFrom(refreshedEvent) {
        this.journal = refreshedEvent.journal;
        this.patronSignups = refreshedEvent.patronSignups;
        this.cancelledSignups = refreshedEvent.cancelledSignups;
        this.manualSignups = refreshedEvent.manualSignups;
    }

    patronSignupCount() {
        if (!this.patronSignups)
            return 0;
        return this.patronSignups.reduce((acc, p) => acc + p.patronCount, 0);
    }

    manualSignupCount() {
        if (!this.manualSignups)
            return 0;
        return this.manualSignups.reduce((acc, p) => acc + p.patronCount, 0);
    }

    cancelledSignupCount() {
        if (!this.cancelledSignups)
            return 0;
        return this.cancelledSignups.reduce((acc, p) => acc + p.patronCount, 0);
    }


    totalPatronCount() {
        return this.patronSignupCount() + this.manualSignupCount();
    }

    hasPatrons() {
        return super.hasPatrons() && this.totalPatronCount() > 0;   
    }

    checkedInCount() {
        let count = 0;
        if (this.patronSignups)
            count += this.patronSignups.reduce((acc, p) => acc + (p.checkedInCount ? p.checkedInCount : 0), 0);
        if (this.manualSignups)
            count += this.manualSignups.reduce((acc, p) => acc + (p.checkedInCount ? p.checkedInCount : 0), 0);
        return count;
    }

    hasStarted() {
        const now = DateConversions.now(Global.getTimezone());
        return this.start <= now;
    }

    hasFinished() { 
        const now = DateConversions.now(Global.getTimezone());
        return this.end <= now;
    }

    isLatePatron(patron) {
        const patronBookDate = patron.patronBookDate();
        if (!patronBookDate)
            return false;

        return patronBookDate >= this.start;
    }   


    symbol() {
        let text = super.symbol();
        if (this.state === "ACTIVE")
            return text.replace("💚", "💙");

        return text;
    }


    remainingTimeMinutes() {
        if (!this.signup)
            return 0;
        
        // Get the current time, round to the next minute
        const now = DateConversions.now(Global.getTimezone()).startOf('minute').plus({minutes: 1});

        const signupEndDate = this.start.minus({minutes: this.signup.closesMinutesBefore ? this.signup.closesMinutesBefore : 0});
        return DateConversions.duration(now, signupEndDate) / 60;
    }


    spacesLeft() {

        if (this.frontEndSpacesLeft != null)        // override spacesLeft with frontEndSpacesLeft
            return this.frontEndSpacesLeft;

        if (!this.signup || !this.patronSignups)
            return 0;

        return this.signup.maxSignups - this.patronSignupCount();
    }

    details() {

        const eventInPast = this.start < DateConversions.now(Global.getTimezone());

        const eventOwnerStr = this.ownerName ? "<div>Event Owner: " + this.ownerName + "</div>" : "";
        const visibilityStr = this.visibility ? "<div>Visibility: " + this.visibility + "</div>" : "";

        let categoryStr = "";
        let signupStart = "";
        let signupEnd = "";
        let manualSignups = "";
        let spacesLeftStr = "";
        let locationStr = "";
        
        if (this.locationString)
            locationStr = "<div>Location: " + this.locationString + "</div>";       // for patron display only

        if (this.signup) {
            if (this.signup.category)
                categoryStr ="<div>Category: " + this.signup.category + "</div>";
        }
        if (this.signup && !eventInPast) {

            const signupStartDate = this.signup.opensMinutesBefore ? this.start.minus({minutes: this.signup.opensMinutesBefore}) : null;
            const signupEndDate = this.signup.closesMinutesBefore ? this.start.minus({minutes: this.signup.closesMinutesBefore}) : null;

            if (signupStartDate && this.signup.patronSignupEnabled) {
                signupStart = "<div>Signup Begins: " + DateConversions.dateTimeStr(signupStartDate) + "</div>";
            }
            else if (this.signup.patronSignupEnabled)
                signupStart = "<div>Signup Enabled: Patrons can sign up anytime</div>";
            else
                signupStart = "<div>Signup Disabled</div>";

            if (signupEndDate && this.signup.patronSignupEnabled)
                signupEnd = "<div>Signup Closes: " + DateConversions.dateTimeStr(signupEndDate) + "</div>";
            else if (this.signup.patronSignupEnabled)
                signupEnd = "<div>Signup Closes: When event starts</div>";
        }
 
        if (this.signup)
            spacesLeftStr = "<div>Spaces Left: " + this.spacesLeft() + "</div>";

        if (this.manualSignups && this.manualSignups.length > 0)
            manualSignups = "<div>Manual Signups: " + this.manualSignups.length + "</div>";

        const checkedInStr = "<div style='margin-top: 5px'>Checked In: " + this.checkedInCount() + " / " + this.totalPatronCount() + "</div>"

        return "<div>" + eventOwnerStr + visibilityStr + categoryStr + locationStr + signupStart + signupEnd + "<div style='margin-top: 5px'/>" + manualSignups + spacesLeftStr + checkedInStr + "<div/>";

    }

}